import React, { useState, FormEvent, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useAuth';
import { useChat } from '@/hooks/useChat';
import { Button } from '@/components/ui/button';
import { AIResponseDisplay } from '@/components/ai/AIResponseDisplay';
import { MessageSquare, Send } from '@/components/icons';
import { ModelSelector } from '@/components/ui/modelSelector';
import { ApiKeyService, useApiKeys } from '@/hooks/useApiKeys';
import { Maximize2, Minimize2, X } from 'lucide-react';
import { cn } from '@/lib/utils';

const LoginChatSection: React.FC = () => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const { getApiKeyByService } = useApiKeys();
    const [chatInput, setChatInput] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);
    const chatInputRef = useRef<HTMLTextAreaElement>(null);

    const {
        loading,
        generateAnswer,
        answer,
        error,
        setAnswer,
        selectedModel,
        setSelectedModel,
        clearHistory
    } = useChat({ type: 'chat' }, user);
    const hasOpenRouterKey = Boolean(user && getApiKeyByService(ApiKeyService.OPENROUTER).trim());

    const handleChatSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!chatInput.trim() || !selectedModel || !hasOpenRouterKey) return;

        try {
            const response = await generateAnswer(chatInput);
            if (response) {
                setAnswer(response);
                setChatInput('');
            }
        } catch (error) {
            console.error('Chat error:', error);
        }
    };

    useEffect(() => {
        if (user) {
            setChatInput('');
            setAnswer('');
        }
    }, [user, setAnswer]);

    useEffect(() => {
        const textarea = chatInputRef.current;
        if (!textarea) return;

        textarea.style.height = 'auto';
        const nextHeight = Math.min(textarea.scrollHeight, 160);
        textarea.style.height = `${nextHeight}px`;
        textarea.style.overflowY = textarea.scrollHeight > 160 ? 'auto' : 'hidden';
    }, [chatInput]);

    if (!hasOpenRouterKey) return null;

    return (
        <div className="fixed bottom-5 right-5 z-50">
            {isOpen && user && (
                <section className={cn(
                    "mb-3 flex flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl transition-[width,height] duration-200",
                    isExpanded
                        ? "h-[calc(100vh-7rem)] w-[calc(100vw-2.5rem)] sm:w-[calc(100vw-3rem)]"
                        : "h-[min(680px,calc(100vh-8rem))] w-[min(420px,calc(100vw-2rem))]"
                )}>
                    <header className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
                        <div className="flex min-w-0 items-center gap-2 text-sm font-medium text-gray-700">
                            <MessageSquare className="h-4 w-4 shrink-0 text-purple-600" />
                            <span className="truncate">
                                {t('home.chat.welcome', {
                                    name: user.email?.split('@')[0] || 'Guest'
                                })}
                            </span>
                        </div>
                        <div className="flex items-center gap-1">
                            <button
                                type="button"
                                onClick={() => setIsExpanded(value => !value)}
                                className="rounded-md p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                                aria-label={t(isExpanded ? 'home.chat.collapse' : 'home.chat.expand')}
                                title={t(isExpanded ? 'home.chat.collapse' : 'home.chat.expand')}
                            >
                                {isExpanded ? <Minimize2 className="h-5 w-5" /> : <Maximize2 className="h-5 w-5" />}
                            </button>
                            <button
                                type="button"
                                onClick={() => setIsOpen(false)}
                                className="rounded-md p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                                aria-label={t('home.chat.close')}
                            >
                                <X className="h-5 w-5" />
                            </button>
                        </div>
                    </header>

                    <div className="min-h-0 flex-1 overflow-y-auto px-4 py-2">
                        <AIResponseDisplay
                            loading={loading}
                            content={answer}
                            error={error}
                            emptyMessage={t('home.chat.startPrompt')}
                            className="px-0"
                        />
                    </div>

                    <div className="border-t border-gray-100 px-4 py-3">
                        <ModelSelector
                            selectedModel={selectedModel}
                            onModelChange={setSelectedModel}
                            onRegenerate={clearHistory}
                            loading={loading}
                            disabled={loading || !selectedModel}
                            type="chat"
                        />

                        <form onSubmit={handleChatSubmit} className="flex items-end gap-2">
                            <textarea
                                ref={chatInputRef}
                                value={chatInput}
                                onChange={(e) => setChatInput(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                                        e.preventDefault();
                                        e.currentTarget.form?.requestSubmit();
                                    }
                                }}
                                placeholder={t('home.chat.inputPlaceholder')}
                                rows={1}
                                className="min-h-11 min-w-0 flex-1 resize-none rounded-xl border border-gray-200 px-3 py-2.5 leading-6 focus:outline-none focus:ring-2 focus:ring-purple-500"
                                disabled={loading || !selectedModel}
                            />
                            <Button
                                type="submit"
                                disabled={loading || !selectedModel || !chatInput.trim()}
                                className="h-11 w-11 shrink-0 rounded-xl bg-purple-600 p-0 text-white shadow-sm hover:bg-purple-700 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                                {loading ? (
                                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                                ) : (
                                    <Send className="h-4 w-4" />
                                )}
                            </Button>
                        </form>
                    </div>
                </section>
            )}

            {!isOpen && (
                <Button
                    type="button"
                    onClick={() => setIsOpen(true)}
                    className="ml-auto flex h-14 w-14 rounded-full bg-purple-600 p-0 text-white shadow-xl hover:bg-purple-700"
                    aria-label={t('home.chat.open')}
                >
                    <MessageSquare className="h-6 w-6" />
                </Button>
            )}
        </div>
    );
};

export default LoginChatSection;
